import history from "instantsearch.js/cjs/lib/routers/history";
import merge from "lodash/merge";
import { queryStringToQueryObject, excludeHiddenFacets, getNonQueryObject, queryObjectToSearchState, searchStateToQueryObject } from "../utils/Algolia/utils/query-string.js";
const createAlgoliaRouter = ({
  router,
  facets,
  indexName,
  pageSize,
  sortIndices,
  presetQueryString,
  serverUrl
}) => {
  let lastPushFromThis = false;
  let handler;
  const stateMapping = (index) => {
    return {
      stateToRoute(uiState) {
        const indexUiState = uiState[index];
        if (!indexUiState) {
          return {};
        }
        const queryObject = searchStateToQueryObject(indexUiState, {
          facets,
          sortIndices,
          pageSize,
          presetQueryObject: {}
        });
        return queryObject;
      },
      routeToState(routeState = {}) {
        const state = queryObjectToSearchState(routeState, {
          facets,
          sortIndices,
          pageSize,
          presetQueryObject: {}
        });
        if (state.configure) {
          const page = state.configure.page ?? 1;
          state.configure.page = page;
          state.configure.offset = Math.min((page - 1) * pageSize, 999);
          state.configure.length = pageSize;
        }
        return {
          [index]: state
        };
      }
    };
  };
  return {
    router: history({
      getLocation() {
        if (typeof window === "undefined") {
          return new URL(serverUrl);
        }
        return window.location;
      },
      /**
       * This will get called whenever Algolia wants to update the URL of the page (facet changes, pagination etc)
       * Here we make sure we ONLY replace the URL using NextJs router
       * TODO: revisit this when client side routing is working in the codebase
       */
      push(url) {
        const parsedUrl = new URL(url);
        const [pathWithoutHash, hash] = (router.asPath || "").split("#");
        const pathname = pathWithoutHash?.split("?")[0];
        const query = parsedUrl.searchParams.toString();
        router.replace(
          { pathname: router.pathname, query },
          { pathname, query, hash },
          { shallow: true }
        );
        lastPushFromThis = true;
      },
      /**
       * Creates URL from search state. Algolia will call this after we are done
       * mapping our state. Great step for removing unwanted facets and URL cleanup
       */
      createURL({ routeState, location: { href }, qsModule }) {
        const filteredState = excludeHiddenFacets(routeState, { facets });
        const search = qsModule.stringify(filteredState, {
          arrayFormat: "repeat",
          encoder: (str) => encodeURIComponent(str)
        });
        const url = new URL(href);
        url.search = search;
        const nonAlgoliaParams = getNonQueryObject(href, { facets });
        const urlParams = new URLSearchParams(url.search);
        Object.entries(nonAlgoliaParams).forEach(([key, value]) => {
          if (Array.isArray(value)) {
            value.forEach((item) => urlParams.append(key, item));
          } else {
            urlParams.append(key, value);
          }
        });
        url.search = urlParams.toString();
        return url.toString();
      },
      /**
       * This will get called every time users load or reload the page, or click the browser’s back or next buttons.
       * Parses URL query parameters into algolia search state (server + client)
       * The result will be passed to our state mapping for further processing
       */
      parseURL({ location }) {
        const presetQueryObject = queryStringToQueryObject(presetQueryString, { facets });
        const urlQueryObject = queryStringToQueryObject(location.search, { facets });
        return merge(urlQueryObject, presetQueryObject);
      },
      start(onUpdate) {
        if (typeof window === "undefined") {
          return void 0;
        }
        const initialPathname = router.pathname;
        handler = () => {
          if (router.pathname === initialPathname && !lastPushFromThis) {
            onUpdate();
          }
          lastPushFromThis = false;
        };
        router.events.on("routeChangeComplete", handler);
      },
      dispose() {
        if (handler) {
          router.events.off("routeChangeComplete", handler);
        }
      },
      cleanUrlOnDispose: true
    }),
    stateMapping: stateMapping(indexName)
  };
};
export {
  createAlgoliaRouter
};
//# sourceMappingURL=create-algolia-router.js.map
